import { Button, Card, Empty, Space, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router';

import { ProductState } from 'protocol/api/billing_new/dto_product_new_pb';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { DisabledActionTooltip, TextLink } from '@/shared/ui';

import {
  billingModel,
  getSubscriptionEndMillis,
  getSubscriptionStartMillis,
  i18n as billingI18n,
  RemoveProductModal,
  serviceIsVisible,
  SubscriptionActivationLimitTriggerOption,
  useRemoveProductModal,
  useTurnOffAutoRenewModal,
} from '@/entities/billing';
import { getBillingProductBlockReason, organizationModel } from '@/entities/organization';

import { PackageBar } from '@/features/packageBar';
import { SubscriptionSummary } from '@/features/subscriptionSummary';

import { i18n } from '../../config';
import { getAddSubscriptionBlockReason } from '../../lib';

const { Title } = Typography;

type CurrentSubscriptionProps = {
  organizationID: string;
};

const selectAccountInfoLoadingState =
  billingModel.selectors.getLoadingStateSelector('accountInfo');

// TODO: Remove Number constructor when in protocol fix bigint
export const CurrentSubscription = (props: CurrentSubscriptionProps) => {
  const { organizationID } = props;
  const { formatDate, formatMessage } = useIntl();

  const { ID: accountID } = useAppSelector(
    billingModel.selectors.selectAccountInfo,
  );
  const currentSubscription = useAppSelector(
    billingModel.selectors.selectCurrentSubscription,
  );
  const scheduledSubscription = useAppSelector(
    billingModel.selectors.selectScheduledSubscription,
  );
  const accountInfoLoadingState = useAppSelector(selectAccountInfoLoadingState);
  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );

  const openTurnOffAutoRenewModal = useTurnOffAutoRenewModal();
  const removeSubscriptionModal = useRemoveProductModal();

  const isCurrentSubscription = Boolean(currentSubscription);
  const addSubscriptionPath = generatePath(PATHS.addSubscription, {
    organizationID,
  });

  const canEditProducts = Boolean(
    organization?.YourPermissions?.BillingCanEditProducts,
  );

  const addSubscriptionBlockReason = getAddSubscriptionBlockReason({
    accountLoadingState: accountInfoLoadingState,
    canEditProducts,
    hasScheduledSubscription: Boolean(scheduledSubscription),
    isCurrentSubscriptionAutoRenewing: Boolean(
      currentSubscription?.AutoRenewal,
    ),
  });

  // Also covers "Remove" and "Turn off auto-renew": the backend gates all
  // three the same way, so there is no reason beyond the ones already
  // covered by getAddSubscriptionBlockReason above.
  const manageSubscriptionBlockReason = getBillingProductBlockReason({
    canEditProducts,
  });
  const manageBlockedTooltip = manageSubscriptionBlockReason
    ? formatMessage(
        i18n.manageSubscriptionBlockReasonMessages[
          manageSubscriptionBlockReason
        ],
      )
    : undefined;

  const renderRemoveButton = (subscriptionID: string) => {
    const button = (
      <Button
        key="remove"
        type="primary"
        disabled={Boolean(manageSubscriptionBlockReason)}
        onClick={() =>
          removeSubscriptionModal.openModal({
            productID: subscriptionID,
            accountID,
          })
        }
      >
        <FormattedMessage
          id="currentSubscription.removeSubscription"
          defaultMessage="Remove"
        />
      </Button>
    );

    if (!manageBlockedTooltip) {
      return button;
    }

    return (
      <DisabledActionTooltip key="remove" title={manageBlockedTooltip}>
        {button}
      </DisabledActionTooltip>
    );
  };

  const renderCancelAutoRenewButton = (
    subscriptionID: string,
    isAutoRenewal: boolean,
  ) => {
    const button = (
      <Button
        key="cancel"
        disabled={!isAutoRenewal || Boolean(manageSubscriptionBlockReason)}
        type="text"
        onClick={() =>
          openTurnOffAutoRenewModal({
            subscriptionID,
            accountID,
            canEditProducts,
          })
        }
      >
        <FormattedMessage
          id="currentSubscription.cancelSubscription"
          defaultMessage="Turn off auto-renew"
        />
      </Button>
    );

    if (!manageBlockedTooltip) {
      return button;
    }

    return (
      <DisabledActionTooltip key="cancel" title={manageBlockedTooltip}>
        {button}
      </DisabledActionTooltip>
    );
  };

  const scheduledActivationTrigger =
    scheduledSubscription?.ActivateOnLimitTrigger
      ? billingI18n.subscriptionActivationLimitTriggerMessages[
          scheduledSubscription.ActivateOnLimitTrigger as SubscriptionActivationLimitTriggerOption
        ]
      : undefined;

  const renderSubscriptionServices = (subscription: ProductState) =>
    subscription.Services.filter((service) =>
      organization?.YourPermissions?.BillingCanEditProducts
        ? true
        : serviceIsVisible(service),
    )
      .sort((a, b) => Number(b.Visible) - Number(a.Visible))
      .map((packageItem) => (
        <PackageBar
          key={`${subscription.ID}-${packageItem.Kind.case}-${
            packageItem.Group
          }-${packageItem.Types.toString()}`}
          packageItem={packageItem}
        />
      ));

  return (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      <Card
        style={{ height: '100%' }}
        title={
          <Title level={3} style={{ marginBottom: 0 }}>
            <FormattedMessage
              id="currentSubscription.title"
              defaultMessage="Current subscription"
            />
          </Title>
        }
        bordered={false}
        loading={accountInfoLoadingState !== 'succeeded'}
        actions={
          isCurrentSubscription
            ? [
                renderRemoveButton(currentSubscription.ID),
                renderCancelAutoRenewButton(
                  currentSubscription.ID,
                  Boolean(currentSubscription?.AutoRenewal),
                ),
              ]
            : null
        }
        extra={
          addSubscriptionBlockReason ? (
            <DisabledActionTooltip
              title={formatMessage(
                i18n.addSubscriptionBlockReasonMessages[
                  addSubscriptionBlockReason
                ],
              )}
            >
              <TextLink to={addSubscriptionPath} disabled>
                <FormattedMessage
                  id="currentSubscription.add"
                  defaultMessage="Add"
                />
              </TextLink>
            </DisabledActionTooltip>
          ) : (
            <TextLink to={addSubscriptionPath}>
              <FormattedMessage
                id="currentSubscription.add"
                defaultMessage="Add"
              />
            </TextLink>
          )
        }
      >
        {isCurrentSubscription ? (
          <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            <SubscriptionSummary
              productID={currentSubscription.ID}
              title={currentSubscription.Model?.Name ?? ''}
              currency={currentSubscription.Model?.Currency}
              price={Number(currentSubscription.Model?.Price ?? 0)}
              customPrice={
                currentSubscription.CustomPrice
                  ? Number(currentSubscription.CustomPrice)
                  : undefined
              }
              startDate={getSubscriptionStartMillis(currentSubscription)}
              endDate={getSubscriptionEndMillis(currentSubscription)}
              status={currentSubscription.Status}
              isAutoRenewal={currentSubscription.AutoRenewal}
            />

            {renderSubscriptionServices(currentSubscription)}
          </Space>
        ) : (
          <Empty
            description={
              <FormattedMessage
                id="currentSubscription.noSubscription"
                defaultMessage="No subscription"
              />
            }
          />
        )}
      </Card>

      {scheduledSubscription ? (
        <Card
          bordered={false}
          loading={accountInfoLoadingState !== 'succeeded'}
          title={
            <Title level={3} style={{ marginBottom: 0 }}>
              <FormattedMessage
                id="scheduledSubscription.title"
                defaultMessage="Scheduled subscription"
              />
            </Title>
          }
          actions={[
            renderRemoveButton(scheduledSubscription.ID),
            renderCancelAutoRenewButton(
              scheduledSubscription.ID,
              Boolean(scheduledSubscription.AutoRenewal),
            ),
          ]}
        >
          <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            <SubscriptionSummary
              productID={scheduledSubscription.ID}
              title={scheduledSubscription.Model?.Name ?? ''}
              currency={scheduledSubscription.Model?.Currency}
              price={Number(scheduledSubscription.Model?.Price ?? 0)}
              customPrice={
                scheduledSubscription.CustomPrice
                  ? Number(scheduledSubscription.CustomPrice)
                  : undefined
              }
              startDate={getSubscriptionStartMillis(scheduledSubscription)}
              endDate={getSubscriptionEndMillis(scheduledSubscription)}
              status={scheduledSubscription.Status}
              isAutoRenewal={scheduledSubscription.AutoRenewal}
              showStatusTag
              dateRangeFallback={formatMessage(
                billingI18n.scheduledSubscriptionMessages.noStartDate,
              )}
            />

            {scheduledActivationTrigger ? (
              <Typography.Text type="secondary">
                {formatMessage(
                  billingI18n.scheduledSubscriptionMessages.activationTrigger,
                  { trigger: formatMessage(scheduledActivationTrigger) },
                )}
              </Typography.Text>
            ) : null}

            {scheduledSubscription.Created?.At ? (
              <Typography.Text type="secondary">
                {formatMessage(
                  {
                    id: 'scheduledSubscription.creationDate',
                    defaultMessage: 'Creation date: {date}',
                  },
                  {
                    date: formatDate(
                      scheduledSubscription.Created.At.toDate(),
                      {
                        year: 'numeric',
                        month: 'long',
                        day: '2-digit',
                      },
                    ),
                  },
                )}
              </Typography.Text>
            ) : null}

            {renderSubscriptionServices(scheduledSubscription)}
          </Space>
        </Card>
      ) : null}

      <RemoveProductModal
        visible={removeSubscriptionModal.visible}
        productID={removeSubscriptionModal.productID}
        productType="subscription"
        accountID={removeSubscriptionModal.accountID}
        canEditProducts={canEditProducts}
        onCancel={removeSubscriptionModal.closeModal}
      />
    </Space>
  );
};
